import { useState, useEffect, useRef } from 'react';
import { Card, Button, Input, Select, TextArea, SafeIcon, Toggle } from '../components/UI';
import { loadSettings, saveSettings } from '../data/repository';
import { BusinessSettings, TaxSettings, InvoiceDefaults, AutomationSettings, CurrencySettings } from '../types';
import { FiSave, FiCheckCircle, FiInfo, FiAlertCircle } from 'react-icons/fi';

export const Settings = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'tax' | 'currency' | 'invoice' | 'automation'>('profile');
  const [notification, setNotification] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [business, setBusiness] = useState<BusinessSettings>({} as any);
  const [tax, setTax] = useState<TaxSettings>({} as any);
  const [currency, setCurrency] = useState<CurrencySettings>({} as any);
  const [invDefaults, setInvDefaults] = useState<InvoiceDefaults>({} as any);
  const [auto, setAuto] = useState<AutomationSettings>({} as any);
  const termsEditorRef = useRef<HTMLDivElement | null>(null);

  const escapeTermsHtml = (value: string): string =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const sanitizeTermsHtml = (html: string): string => {
    const template = document.createElement('template');
    template.innerHTML = String(html || '');
    const allowedTags = new Set(['P', 'DIV', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI']);
    
    const cleanNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return escapeTermsHtml(node.textContent || '');
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }
      const element = node as HTMLElement;
      const tag = element.tagName.toUpperCase();
      if (tag === 'SCRIPT' || tag === 'STYLE') {
        return '';
      }
      const children = Array.from(element.childNodes).map(cleanNode).join('');
      if (!allowedTags.has(tag)) {
        return children;
      }
      if (tag === 'BR') {
        return '<br>';
      }
      return `<${tag.toLowerCase()}>${children}</${tag.toLowerCase()}>`;
    };

    return Array.from(template.content.childNodes).map(cleanNode).join('');
  };

  const termsHtmlToPlainText = (html: string): string => {
    const container = document.createElement('div');
    container.innerHTML = sanitizeTermsHtml(html);
    
    const toPlainText = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }
      const element = node as HTMLElement;
      const tag = element.tagName.toUpperCase();
      const children = Array.from(element.childNodes).map(toPlainText).join('');
      if (tag === 'BR') {
        return '\n';
      }
      if (tag === 'LI') {
        const parentTag = element.parentElement?.tagName.toUpperCase();
        if (parentTag === 'OL') {
          const siblings = Array.from(element.parentElement?.children || []);
          const number = siblings.indexOf(element) + 1;
          return `${number}. ${children}\n`;
        }
        return `• ${children}\n`;
      }
      if (tag === 'P' || tag === 'DIV') {
        return `${children}\n`;
      }
      return children;
    };

    return Array.from(container.childNodes)
      .map(toPlainText)
      .join('')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const updateTermsFromEditor = (normaliseEditor = false) => {
    const editor = termsEditorRef.current;
    if (!editor) {
      return;
    }
    const sanitizedHtml = sanitizeTermsHtml(editor.innerHTML);
    if (normaliseEditor && editor.innerHTML !== sanitizedHtml) {
      editor.innerHTML = sanitizedHtml;
    }
    setInvDefaults(current => ({
      ...current,
      paymentTermsHtml: sanitizedHtml,
      paymentTerms: termsHtmlToPlainText(sanitizedHtml)
    }));
  };

  const applyTermsCommand = (command: string) => {
    termsEditorRef.current?.focus();
    document.execCommand(command, false);
    updateTermsFromEditor(true);
  };

  useEffect(() => {
    const editor = termsEditorRef.current;
    if (activeTab !== 'invoice' || !editor) {
      return;
    }
    if (document.activeElement === editor) {
      return;
    }
    const savedHtml = invDefaults.paymentTermsHtml?.trim();
    const fallbackHtml = escapeTermsHtml(invDefaults.paymentTerms || '').replace(/\n/g, '<br>');
    const nextHtml = savedHtml || fallbackHtml;
    if (editor.innerHTML !== nextHtml) {
      editor.innerHTML = nextHtml;
    }
  }, [activeTab, invDefaults.paymentTermsHtml, invDefaults.paymentTerms]);

  const loadData = async () => {
    try {
      const s = await loadSettings();
      setBusiness(s.business);
      setTax(s.tax);
      setCurrency(s.currency);
      setInvDefaults(s.invoice);
      setAuto(s.automation);
    } catch (err: any) {
      setNotification({ msg: `Unable to load settings: ${err.message}`, type: 'error' });
    }
  };

  // Add workspace refresh listener
  useEffect(() => {
    loadData();

    const handleRefresh = () => {
      loadData();
    };

    window.addEventListener('invoiceflow:refresh-data', handleRefresh);

    return () => {
      window.removeEventListener('invoiceflow:refresh-data', handleRefresh);
    };
  }, []);

  const showNotification = (msg: string, type: 'success' | 'error') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSave = async () => {
    try {
      await saveSettings({ business, tax, currency, invoice: invDefaults, automation: auto });
      const refreshed = await loadSettings();
      setBusiness(refreshed.business);
      setTax(refreshed.tax);
      setCurrency(refreshed.currency);
      setInvDefaults(refreshed.invoice);
      setAuto(refreshed.automation);
      
      const attemptedUploadedLogo = (business.logoDataUrl || '').trim();
      const savedUploadedLogo = (refreshed.business.logoDataUrl || '').trim();
      
      if (attemptedUploadedLogo && !savedUploadedLogo) {
        throw new Error('The uploaded logo was not retained after saving. Check the PocketBase business_settings record.');
      }
      
      showNotification(savedUploadedLogo ? 'Settings saved successfully. Uploaded invoice logo confirmed.' : 'Settings saved successfully.', 'success');
    } catch (err: any) {
      showNotification(`Unable to save settings: ${err.message}`, 'error');
    }
  };

  const normalizeLogoForPdf = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Unable to read the selected logo file.'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Unable to decode the selected logo. Use a standard PNG or JPEG file.'));
        img.onload = () => {
          try {
            const sourceWidth = img.naturalWidth || img.width;
            const sourceHeight = img.naturalHeight || img.height;
            if (!sourceWidth || !sourceHeight) {
              reject(new Error('The selected logo has invalid dimensions.'));
              return;
            }
            const maxWidth = 900;
            const maxHeight = 320;
            const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
            const width = Math.max(1, Math.round(sourceWidth * scale));
            const height = Math.max(1, Math.round(sourceHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Unable to prepare the logo image.'));
              return;
            }
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            const normalizedDataUrl = canvas.toDataURL('image/png');
            if (!normalizedDataUrl.startsWith('data:image/png;base64,')) {
              reject(new Error('Unable to convert the logo into a PDF-safe PNG.'));
              return;
            }
            resolve(normalizedDataUrl);
          } catch (error) {
            reject(error instanceof Error ? error : new Error('Unable to prepare the logo image.'));
          }
        };
        img.src = String(reader.result || '');
      };
      reader.readAsDataURL(file);
    });
  };

  const updateRecipientBehavior = (updates: Partial<{ useClientBilling: boolean; includeContacts: boolean; allowManual: boolean; }>) => {
    setAuto(current => ({
      ...current,
      recipientBehavior: {
        useClientBilling: true,
        includeContacts: true,
        allowManual: true,
        ...current.recipientBehavior,
        ...updates,
      },
    }));
  };

  return (
    <div className="w-full max-w-6xl min-w-0 mx-auto space-y-6 overflow-x-hidden">
      {notification && (
        <div className={`fixed left-4 right-4 bottom-20 top-auto md:left-auto md:right-8 md:top-8 md:bottom-auto p-4 md:p-6 ${notification.type === 'success' ? 'bg-teal-600' : 'bg-rose-600'} text-white rounded-xl md:rounded-2xl shadow-2xl flex items-center gap-3 z-[100] animate-in slide-in-from-right`}>
          {notification.type === 'success' ? <FiCheckCircle className="text-xl" /> : <FiAlertCircle className="text-xl" />}
          <span className="font-bold">{notification.msg}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <Button onClick={handleSave} className="w-full sm:w-auto px-6">
          <FiSave /> Save All
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
        <Card className="col-span-1 p-2 flex flex-col gap-1 h-fit">
          {[
            { id: 'profile', label: 'Business Profile' },
            { id: 'tax', label: 'Tax Settings' },
            { id: 'currency', label: 'Currency' },
            { id: 'invoice', label: 'Invoice Defaults' },
            { id: 'automation', label: 'Automation' },
          ].map(t => (
            <Button
              key={t.id}
              variant={activeTab === t.id ? 'primary' : 'ghost'}
              onClick={() => setActiveTab(t.id as any)}
              className="justify-start font-bold text-sm"
            >
              {t.label}
            </Button>
          ))}
        </Card>

        <Card className="col-span-1 lg:col-span-3 p-4 sm:p-6 min-h-[600px]">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold border-b border-gray-100 pb-2">Business Profile</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Business Name" value={business.businessName || ''} onChange={(e: any) => setBusiness({ ...business, businessName: e.target.value })} />
                <Input label="Trading Name" value={business.tradingName || ''} onChange={(e: any) => setBusiness({ ...business, tradingName: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Input label="Business Logo URL" type="url" value={business.logoUrl || ''} onChange={(e: any) => setBusiness({ ...business, logoUrl: e.target.value })} placeholder="https://example.com/logo.png" />
                <p className="text-[11px] text-slate-500 font-medium ml-1">Use a publicly accessible PNG or JPEG logo URL. The logo will appear on generated invoice PDFs.</p>
              </div>
              <div className="space-y-2 pt-4 border-t border-gray-100">
                <label className="text-[13px] font-bold text-gray-700 ml-0.5 block mb-1.5">Upload Invoice Logo</label>
                <div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="w-full max-w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!['image/png', 'image/jpeg'].includes(file.type)) {
                        showNotification('Use a standard PNG or JPEG logo file.', 'error');
                        e.target.value = '';
                        return;
                      }
                      if (file.size > 350 * 1024) {
                        showNotification('Logo file is too large. Use a PNG or JPEG smaller than 350 KB.', 'error');
                        e.target.value = '';
                        return;
                      }
                      try {
                        const normalizedLogo = await normalizeLogoForPdf(file);
                        setBusiness(current => ({ ...current, logoDataUrl: normalizedLogo }));
                        showNotification('Logo prepared successfully. Click Save All to apply it to invoice PDFs.', 'success');
                      } catch (error) {
                        showNotification(error instanceof Error ? error.message : 'Unable to prepare the selected logo.', 'error');
                      } finally {
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
                <p className="text-[11px] text-slate-500 font-medium ml-1">Upload a compact PNG or JPEG logo for reliable invoice embedding. The uploaded logo is saved with your business settings and takes priority over the external URL.</p>
                {business.logoDataUrl && (
                  <>
                    <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-4">
                      <div className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                        <img src={business.logoDataUrl} alt="Uploaded logo preview" className="h-10 object-contain" />
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => setBusiness({ ...business, logoDataUrl: '' })}>Remove Uploaded Logo</Button>
                    </div>
                    <p className="text-[11px] font-semibold text-emerald-700 mt-2">Uploaded invoice logo is available in the saved business profile.</p>
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <Input label="ABN" value={business.abn || ''} onChange={(e: any) => setBusiness({ ...business, abn: e.target.value })} />
                <Input label="Website" value={business.website || ''} onChange={(e: any) => setBusiness({ ...business, website: e.target.value })} />
                <Input label="Business Email" type="email" value={business.email || ''} onChange={(e: any) => setBusiness({ ...business, email: e.target.value })} />
                <Input label="Phone" value={business.phone || ''} onChange={(e: any) => setBusiness({ ...business, phone: e.target.value })} />
              </div>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700">Official Address</h4>
                <Input label="Address Line 1" value={business.addressLine1 || ''} onChange={(e: any) => setBusiness({ ...business, addressLine1: e.target.value })} />
                <Input label="Address Line 2" value={business.addressLine2 || ''} onChange={(e: any) => setBusiness({ ...business, addressLine2: e.target.value })} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input label="Suburb" value={business.suburb || ''} onChange={(e: any) => setBusiness({ ...business, suburb: e.target.value })} />
                  <Input label="State" value={business.state || ''} onChange={(e: any) => setBusiness({ ...business, state: e.target.value })} />
                  <Input label="Postcode" value={business.postcode || ''} onChange={(e: any) => setBusiness({ ...business, postcode: e.target.value })} />
                  <Input label="Country" value={business.country || ''} onChange={(e: any) => setBusiness({ ...business, country: e.target.value })} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tax' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold border-b border-gray-100 pb-2">Tax Settings</h3>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <Toggle label="GST Registered" description="Enable if your business is registered for GST" checked={tax.gstRegistered} onChange={(v: boolean) => setTax({ ...tax, gstRegistered: v })} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Default GST Rate (%)" type="number" step="0.1" value={(tax.defaultGstRate || 0) * 100} onChange={(e: any) => setTax({ ...tax, defaultGstRate: parseFloat(e.target.value) / 100 })} />
                <Select label="Default Tax Treatment" value={tax.defaultTaxTreatment} options={[
                  { label: 'GST 10%', value: 'gst_10' },
                  { label: 'GST Free', value: 'gst_free' },
                  { label: 'No GST', value: 'no_gst' },
                  { label: 'Export', value: 'export' },
                  { label: 'Custom Rate', value: 'custom' },
                ]} onChange={(e: any) => setTax({ ...tax, defaultTaxTreatment: e.target.value })} />
              </div>
              {tax.defaultTaxTreatment === 'custom' && (
                <div className="w-1/2 pr-2">
                  <Input label="Custom Tax Rate (%)" type="number" step="0.01" value={(tax.customTaxRate || 0) * 100} onChange={(e: any) => setTax({ ...tax, customTaxRate: parseFloat(e.target.value) / 100 })} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'currency' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold border-b border-gray-100 pb-2">Currency</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select label="Default Currency" value={currency.defaultCurrency || 'AUD'} options={[
                  { label: 'AUD', value: 'AUD' },
                  { label: 'USD', value: 'USD' },
                ]} onChange={(e: any) => setCurrency({ ...currency, defaultCurrency: e.target.value, supportedCurrencies: ['AUD', 'USD'] })} />
                <Select label="Decimal Places" value={currency.decimalPlaces?.toString() || '2'} options={[
                  { label: '2', value: '2' },
                  { label: '0', value: '0' },
                ]} onChange={(e: any) => setCurrency({ ...currency, decimalPlaces: parseInt(e.target.value) as 0 | 2 })} />
              </div>
            </div>
          )}

          {activeTab === 'invoice' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold border-b border-gray-100 pb-2">Invoice Defaults</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label="Invoice Prefix" value={invDefaults.prefix || ''} onChange={(e: any) => setInvDefaults({ ...invDefaults, prefix: e.target.value })} />
                <Input label="Next Invoice Number" type="number" value={invDefaults.nextNumber || 1001} onChange={(e: any) => setInvDefaults({ ...invDefaults, nextNumber: parseInt(e.target.value) || 1001 })} />
                <Input label="Default Due Days" type="number" value={invDefaults.dueDays || 14} onChange={(e: any) => setInvDefaults({ ...invDefaults, dueDays: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700">Invoice PDF Appearance</h4>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[13px] font-bold text-gray-700 ml-0.5">Invoice PDF Accent Colour</label>
                    <input
                      type="color"
                      value={invDefaults.pdfAccentColor || '#0f766e'}
                      onChange={(e: any) => setInvDefaults({ ...invDefaults, pdfAccentColor: e.target.value })}
                      className="h-10 w-20 rounded-lg border border-gray-300 bg-white p-1 cursor-pointer"
                    />
                  </div>
                  <Input label="Accent Colour Hex" value={invDefaults.pdfAccentColor || '#0f766e'} onChange={(e: any) => setInvDefaults({ ...invDefaults, pdfAccentColor: e.target.value })} placeholder="#0f766e" className="w-full sm:max-w-[180px]" />
                </div>
                <p className="text-[11px] text-gray-500 ml-0.5">This colour is used only on generated invoice PDFs. It does not change the application interface.</p>
              </div>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700">Banking Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input label="Bank Account Name" value={invDefaults.bankName || ''} onChange={(e: any) => setInvDefaults({ ...invDefaults, bankName: e.target.value })} />
                  <Input label="BSB" value={invDefaults.bsb || ''} onChange={(e: any) => setInvDefaults({ ...invDefaults, bsb: e.target.value })} />
                  <Input label="Account Number" value={invDefaults.accountNumber || ''} onChange={(e: any) => setInvDefaults({ ...invDefaults, accountNumber: e.target.value })} />
                </div>
                <div className="w-full md:w-1/3">
                  <Select label="Payment Reference Format" value={invDefaults.paymentReferenceFormat || 'invoice_number'} options={[
                    { label: 'Invoice Number (e.g. INV-1001)', value: 'invoice_number' },
                    { label: 'Client Name + Inv #', value: 'client_name_inv' },
                    { label: 'Custom', value: 'custom' },
                  ]} onChange={(e: any) => setInvDefaults({ ...invDefaults, paymentReferenceFormat: e.target.value })} />
                </div>
              </div>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-gray-700">PayID Payments</h4>
                    <p className="text-[11px] leading-relaxed text-gray-500">Optionally display the official PayID brandmark and your registered PayID details on newly generated invoice PDFs. This uses the existing invoice settings record; no backend schema change is required.</p>
                  </div>
                  <div className="w-full sm:max-w-xs">
                    <Toggle
                      label="Show PayID on invoices"
                      description="Disabled by default. Existing issued PDFs are not changed."
                      checked={invDefaults.payIdEnabled === true}
                      onChange={(value: boolean) => setInvDefaults({ ...invDefaults, payIdEnabled: value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="PayID"
                    value={invDefaults.payId || ''}
                    onChange={(e: any) => setInvDefaults({ ...invDefaults, payId: e.target.value })}
                    placeholder="e.g. accounts@example.com or mobile number"
                  />
                  <Input
                    label="Registered PayID Name"
                    value={invDefaults.payIdName || ''}
                    onChange={(e: any) => setInvDefaults({ ...invDefaults, payIdName: e.target.value })}
                    placeholder="Name shown when the payer verifies the PayID"
                  />
                </div>
                <p className="text-[11px] leading-relaxed text-gray-500">The PayID block appears on an invoice PDF only when this option is enabled and both fields are completed. The invoice number is shown as the payment reference, and the official PayID trademark notice is included automatically. Verify the PayID and registered name before enabling the option.</p>
              </div>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="text-[13px] font-bold text-gray-700 ml-0.5">Global Terms & Conditions</label>
                  <p className="text-[11px] text-gray-500 mb-2 ml-0.5">These terms are copied into each new invoice as a snapshot. Editing the global terms later will not alter existing invoices.</p>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-slate-50 p-2">
                      <button type="button" onClick={() => applyTermsCommand('bold')} className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50">Bold</button>
                      <button type="button" onClick={() => applyTermsCommand('italic')} className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs italic text-gray-700 hover:bg-gray-50">Italic</button>
                      <button type="button" onClick={() => applyTermsCommand('underline')} className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs underline text-gray-700 hover:bg-gray-50">Underline</button>
                      <button type="button" onClick={() => applyTermsCommand('insertUnorderedList')} className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">Bullets</button>
                      <button type="button" onClick={() => applyTermsCommand('insertOrderedList')} className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">Numbered List</button>
                      <button type="button" onClick={() => applyTermsCommand('removeFormat')} className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">Clear Formatting</button>
                    </div>
                    <div
                      ref={termsEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={() => updateTermsFromEditor()}
                      onBlur={() => updateTermsFromEditor(true)}
                      className="min-h-[180px] max-h-[520px] resize-y overflow-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                    />
                    <p className="text-[11px] text-gray-500">Formatting is saved safely. A plain-text compatibility version is maintained automatically for the existing invoice PDF and snapshot workflow.</p>
                  </div>
                </div>
                <TextArea label="Default Client-Facing Note" value={invDefaults.defaultPublicNote || ''} onChange={(e: any) => setInvDefaults({ ...invDefaults, defaultPublicNote: e.target.value })} rows={2} />
                <TextArea label="Default Internal Note" value={invDefaults.defaultInternalNote || ''} onChange={(e: any) => setInvDefaults({ ...invDefaults, defaultInternalNote: e.target.value })} rows={2} />
              </div>
            </div>
          )}

          {activeTab === 'automation' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold border-b border-gray-100 pb-2">Automation</h3>
              <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 flex gap-3">
                <SafeIcon icon={FiInfo} className="text-sky-600 mt-0.5" />
                <div className="text-sm text-sky-900">
                  <p className="font-bold mb-1">External Automation Integration</p>
                  <p>Invoice emails are sent by external automation. Store SMTP or EmailIt credentials securely in Make/Boost.space or Google Apps Script Properties. <strong>Do not paste secrets into this app.</strong></p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="From Name" value={auto.fromName || ''} onChange={(e: any) => setAuto({ ...auto, fromName: e.target.value })} />
                <Input label="Reply-To Email" type="email" value={auto.replyToEmail || ''} onChange={(e: any) => setAuto({ ...auto, replyToEmail: e.target.value })} />
                <Input label="Default CC" value={auto.defaultCc || ''} onChange={(e: any) => setAuto({ ...auto, defaultCc: e.target.value })} />
                <Input label="Default BCC" value={auto.defaultBcc || ''} onChange={(e: any) => setAuto({ ...auto, defaultBcc: e.target.value })} />
                <Input label="Permanent Audit CC" value={auto.permanentAuditCc || ''} onChange={(e: any) => setAuto({ ...auto, permanentAuditCc: e.target.value })} placeholder="Optional visible audit copy" />
                <Input label="Permanent Audit BCC" value={auto.permanentAuditBcc || ''} onChange={(e: any) => setAuto({ ...auto, permanentAuditBcc: e.target.value })} placeholder="Recommended internal invoice archive" />
                <Select label="Provider" value={auto.provider || 'none'} options={[
                  { label: 'None', value: 'none' },
                  { label: 'Make / Boost.space', value: 'make_boost' },
                  { label: 'Google Apps Script', value: 'google_apps_script' },
                  { label: 'Other', value: 'other' },
                ]} onChange={(e: any) => setAuto({ ...auto, provider: e.target.value })} />
                <Select label="Sending Mode" value={auto.sendingMode || 'disabled'} options={[
                  { label: 'Disabled', value: 'disabled' },
                  { label: 'External Queue', value: 'external_queue' },
                ]} onChange={(e: any) => setAuto({ ...auto, sendingMode: e.target.value })} />
                <Input label="Polling Frequency Minutes" type="number" value={auto.pollingFrequencyMinutes || ''} onChange={(e: any) => setAuto({ ...auto, pollingFrequencyMinutes: parseInt(e.target.value) || 0 })} />
              </div>
              <p className="text-[11px] text-gray-500">Permanent Audit CC and BCC addresses are appended automatically when an invoice email, payment reminder, or recurring invoice email is queued. Permanent Audit BCC is recommended for an internal invoice archive mailbox.</p>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700">Recipient Behavior</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <Toggle label="Use Client Billing Email" checked={auto.recipientBehavior?.useClientBilling ?? true} onChange={(v: boolean) => updateRecipientBehavior({ useClientBilling: v })} />
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <Toggle label="Include Client Contacts" checked={auto.recipientBehavior?.includeContacts ?? true} onChange={(v: boolean) => updateRecipientBehavior({ includeContacts: v })} />
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <Toggle label="Allow Manual Recipients" checked={auto.recipientBehavior?.allowManual ?? true} onChange={(v: boolean) => updateRecipientBehavior({ allowManual: v })} />
                  </div>
                </div>
              </div>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700">Accounts Contact for Invoice Emails</h4>
                <p className="text-[11px] text-gray-500 mb-2 ml-0.5">This contact is inserted automatically into drafted invoice emails. Update it here once and new Send Invoice drafts will use the saved details.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Accounts Contact Name" value={auto.accountsContactName || ''} onChange={(e: any) => setAuto({ ...auto, accountsContactName: e.target.value })} />
                  <Input label="Accounts Contact Title" value={auto.accountsContactTitle || ''} onChange={(e: any) => setAuto({ ...auto, accountsContactTitle: e.target.value })} />
                  <Input label="Accounts Contact Email" type="email" value={auto.accountsContactEmail || ''} onChange={(e: any) => setAuto({ ...auto, accountsContactEmail: e.target.value })} />
                  <Input label="Accounts Contact Phone" value={auto.accountsContactPhone || ''} onChange={(e: any) => setAuto({ ...auto, accountsContactPhone: e.target.value })} />
                </div>
              </div>
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700">Default Invoice Email Draft</h4>
                <p className="text-[11px] text-gray-500 mb-2 ml-0.5">Use the supported variables below. The draft is populated automatically when Send Invoice opens and remains editable before sending.</p>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-[10px] font-mono text-slate-600 flex flex-wrap gap-2">
                  <span>{`{{client_name}}`}</span>
                  <span>{`{{business_name}}`}</span>
                  <span>{`{{invoice_number}}`}</span>
                  <span>{`{{invoice_total}}`}</span>
                  <span>{`{{balance_due}}`}</span>
                  <span>{`{{due_date}}`}</span>
                  <span>{`{{payment_terms}}`}</span>
                  <span>{`{{job_name}}`}</span>
                  <span>{`{{accounts_contact_name}}`}</span>
                  <span>{`{{accounts_contact_title}}`}</span>
                  <span>{`{{accounts_contact_email}}`}</span>
                  <span>{`{{accounts_contact_phone}}`}</span>
                  <span>{`{{accounts_contact_signature}}`}</span>
                </div>
                <Input label="Default Invoice Email Subject" value={auto.invoiceEmailSubjectTemplate || ''} onChange={(e: any) => setAuto({ ...auto, invoiceEmailSubjectTemplate: e.target.value })} placeholder="Invoice {{invoice_number}} from {{business_name}} | Due {{due_date}}" />
                <TextArea label="Default Invoice Email Message" value={auto.invoiceEmailBodyTemplate || ''} onChange={(e: any) => setAuto({ ...auto, invoiceEmailBodyTemplate: e.target.value })} rows={15} placeholder={`Hi {{client_name}},\n\nThank you for working with {{business_name}}.\n\nPlease find attached invoice {{invoice_number}} for {{invoice_total}}.\n\nInvoice summary:\nInvoice number: {{invoice_number}}\nAmount due: {{balance_due}}\nDue date: {{due_date}}\n\nIf you have any questions regarding this invoice or payment arrangements, please contact:\n\n{{accounts_contact_signature}}\n\nKind regards,\n{{accounts_contact_name}}\n{{accounts_contact_title}}\n{{business_name}}`} />
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};